import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const UPLOAD_URL = 'https://new.trydoobie.com/wp-content/reactpress/apps/doobie/scripts/upload.php';

const FETCH_CART_DETAILS = gql`
  query FetchCartDetails($retailerId: ID!, $checkoutId: ID!) {
    checkout(retailerId: $retailerId, id: $checkoutId) {
      id
      pricingType
      orderType
      priceSummary {
        taxes 
        subtotal
        total
        discounts
        rewards
      }
      items {
        id
        option
        product {
          image      
          name
          id
          brand {
            name
          }
          variants {
            option
            priceRec
            specialPriceRec
          }
        }
        quantity
      }
      redirectUrl
    }
  }
`;

const UPDATE_QUANTITY = gql`
  mutation UpdateQuantity($checkoutId: ID!, $itemId: ID!, $quantity: Int!, $retailerId: ID!) {
    updateQuantity(
      checkoutId: $checkoutId
      itemId: $itemId
      quantity: $quantity
      retailerId: $retailerId
    ) {
      items {
        id
        option
        quantity
      }
      priceSummary {
        taxes
        subtotal
        total
        discounts
        rewards
      }
    }
  }
`;

const REMOVE_ITEM_FROM_CHECKOUT = gql`
  mutation RemoveItemFromCheckout($retailerId: ID!, $checkoutId: ID!, $itemId: ID!) {
    removeItem(retailerId: $retailerId, checkoutId: $checkoutId, itemId: $itemId) {
      id
      pricingType
      orderType
      priceSummary {
        taxes
        subtotal
        total
        discounts
        rewards
      }
      items {
        id
        option
        quantity
        product {
          image
          name
          id
          brand {
            name
          }
          variants {
            option
            priceRec
            specialPriceRec
          }
        }
      }
    }
  }
`;

const CheckoutForm2 = ({ retailerId, checkoutId }) => {
    const { register, handleSubmit } = useForm();
    const { loading, error, data, refetch } = useQuery(FETCH_CART_DETAILS, {
        variables: { retailerId, checkoutId },
    });

    const [loadingSpinner, setLoadingSpinner] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [priceSummary, setPriceSummary] = useState(null);
    const [buttonText, setButtonText] = useState('Place Order');
    const [updateQuantity] = useMutation(UPDATE_QUANTITY);
    const [removeItem] = useMutation(REMOVE_ITEM_FROM_CHECKOUT);
    const [couponCode, setCouponCode] = useState('');
    const [appliedDiscount, setAppliedDiscount] = useState(null);
    const [ourDiscount, setOurDiscount] = useState(0);
    const [tipAmount, setTipAmount] = useState(0);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [complianceFee, setComplianceFee] = useState(0); // Tracks the calculated compliance fee
    const [complianceDetails, setComplianceDetails] = useState(null); // Holds compliance fee details
    const [paymentMethod, setPaymentMethod] = useState(''); // Tracks the selected payment method

    const [deliveryFeeDetails, setDeliveryFeeDetails] = useState({
        scheduled: 0,
        express: 0
    });

    const coupondeductrule = 'subtotal'; // Can be 'subtotal' or 'total'
    let originalTaxRate = 0;

    useEffect(() => {
        const spinnerObserver = new MutationObserver((mutationsList) => {
            for (let mutation of mutationsList) {
                const ajaxSpinner4 = document.querySelector('#gform_ajax_spinner_4.gform-loader');
                const ajaxSpinner5 = document.querySelector('#gform_ajax_spinner_5.gform-loader');
                const strongHoldActive = document.querySelector('#stronghold-pay-iframe');

                if (ajaxSpinner4 || ajaxSpinner5 || strongHoldActive) {
                    setLoadingSpinner(true);
                } else {
                    setLoadingSpinner(false);
                }
            }
        });

        spinnerObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => {
            spinnerObserver.disconnect();
        };
    }, []);

    // Calculate the tax rate from priceSummary once it's valid
    useEffect(() => {
        if (priceSummary?.subtotal && priceSummary?.taxes) {
            originalTaxRate = (priceSummary.taxes / priceSummary.subtotal) * 100;
        }
    }, [priceSummary]);

    // Fetch compliance details, delivery fees, and other store-specific details
    useEffect(() => {
        const almaUrl = "https://www.almapos.com/api/store_checkout_times";

        fetch(almaUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: `token=${retailerId}`
        })
            .then(response => response.json())
            .then(storedetails => {
                var minimum = storedetails.minimum_spend;
                window.minimum = minimum;

                const areaId = localStorage.getItem('areaId');
                const achPercentage = parseFloat(storedetails.areas[areaId].ach_convenience_percentage) / 100;
                const achValue = parseFloat(storedetails.areas[areaId].ach_convenience_value);

                const scheduledDeliveryFee = parseFloat(storedetails.areas[areaId].scheduledDeliveryFee) * 100;
                const expressDeliveryFee = parseFloat(storedetails.areas[areaId].expressDeliveryFee) * 100;

                setComplianceDetails({
                    percentage: achPercentage,
                    value: achValue,
                });

                setDeliveryFeeDetails({
                    scheduled: scheduledDeliveryFee,
                    express: expressDeliveryFee,
                });
            })
            .catch(error => {
                console.error("Error fetching store details:", error);
            });

        if (error) {
            console.error('Error fetching cart details:', error);
        }
        if (data) {
            const newPriceSummary = JSON.parse(JSON.stringify(data.checkout.priceSummary));
            setCartItems(data.checkout.items);
            setPriceSummary(newPriceSummary);
        }
    }, [error, data, retailerId]);

    const calculateDiscountedTotals = () => {
        if (!priceSummary || !originalTaxRate) return; // Avoid calculation if data is missing

        let newSubtotal = priceSummary?.subtotal || 0;
        let discountAmount = 0;

        if (appliedDiscount && coupondeductrule === 'subtotal') {
            discountAmount = (newSubtotal * appliedDiscount.value) / 100;
            newSubtotal -= discountAmount;
        }

        const newTaxes = (newSubtotal * originalTaxRate) / 100;

        return {
            newSubtotal: newSubtotal.toFixed(2),
            newTaxes: newTaxes.toFixed(2),
            discountAmount: discountAmount.toFixed(2)
        };
    };

    useEffect(() => {
        if (priceSummary && appliedDiscount) {
            const { newSubtotal, newTaxes, discountAmount } = calculateDiscountedTotals() || {};

            if (newSubtotal && newTaxes) {
                setPriceSummary((prevSummary) => ({
                    ...prevSummary,
                    subtotal: parseFloat(newSubtotal) * 100,  // Convert back to cents
                    taxes: parseFloat(newTaxes) * 100,        // Convert back to cents
                    discounts: discountAmount * 100           // Store discount in cents
                }));
            }
        }
    }, [appliedDiscount, cartItems]);

    const handleChangeQuantity = async (itemId, newQuantity) => {
        if (newQuantity === 0) {
            try {
                await removeItem({
                    variables: {
                        checkoutId,
                        itemId,
                        retailerId,
                    },
                });
                refetch();
            } catch (error) {
                console.error('Error removing item:', error);
            }
        } else {
            try {
                await updateQuantity({
                    variables: {
                        checkoutId,
                        itemId,
                        quantity: newQuantity,
                        retailerId,
                    },
                });
                refetch();
            } catch (error) {
                console.error('Error updating quantity:', error);
            }
        }
    };

    const handleApplyCoupon = () => {
        const promocode = couponCode;
        const store = retailerId;
        const totalBeforeDiscount = (priceSummary?.subtotal + priceSummary?.taxes) / 100;
        const email = document.querySelector('input[name="input_5"]').value;

        const callbackName = `jsonp_callback_${Math.round(100000 * Math.random())}`;

        window[callbackName] = function(response) {
            delete window[callbackName];
            document.body.removeChild(script);

            if (response.error) {
                alert(response.error);
            } else {
                let discountAmount = 0;

                if (response.type === 'amount') {
                    discountAmount = parseFloat(response.value);
                } else if (response.type === 'percentage') {
                    const discountRate = parseFloat(response.value) / 100;
                    const calculatedDiscount = totalBeforeDiscount * discountRate;
                    discountAmount = parseFloat(calculatedDiscount.toFixed(2));
                }

                discountAmount = Math.min(discountAmount, totalBeforeDiscount);
                setOurDiscount(discountAmount * 100);
                setAppliedDiscount({ code: promocode, value: discountAmount });

                document.querySelector('input[name="input_37"]').value = response.code;
                document.querySelector('input[name="input_38"]').value = response.type;
                document.querySelector('input[name="input_39"]').value = discountAmount.toFixed(2);
            }
        };

        const url = `https://www.almapos.com/api/check_coupon?callback=${callbackName}&code=${promocode}&store=${store}&order_total=${totalBeforeDiscount}&email=${encodeURIComponent(email)}`;

        const script = document.createElement('script');
        script.src = url;
        document.body.appendChild(script);
    };

    const handleRemoveCoupon = () => {
        setCouponCode('');
        setAppliedDiscount(null);
        setOurDiscount(0);

        document.querySelector('input[name="input_37"]').value = '';
        document.querySelector('input[name="input_38"]').value = '';
        document.querySelector('input[name="input_39"]').value = '';

        refetch();
    };

    useEffect(() => {
        if (priceSummary) {
            const orderTotal = (priceSummary.subtotal + priceSummary.taxes - priceSummary.discounts - ourDiscount + deliveryFee) / 100 + complianceFee + tipAmount / 100;
            document.querySelector('input[name="input_30"]').value = orderTotal.toFixed(2);
            document.querySelector('input[name="input_31"]').value = (tipAmount / 100).toFixed(2);

            document.querySelector('input[name="input_30"]').dispatchEvent(new Event('change', { bubbles: true }));
            document.querySelector('input[name="input_31"]').dispatchEvent(new Event('change', { bubbles: true }));
        }
    }, [priceSummary, tipAmount, ourDiscount, deliveryFee, complianceFee]);

    useEffect(() => {
        var minimum = window.minimum;
        const minimumSpend = parseFloat(minimum);
        const totalAmount = ((priceSummary?.subtotal + priceSummary?.taxes - priceSummary?.discounts - ourDiscount + deliveryFee) / 100 + complianceFee + tipAmount / 100);

        const checkAndUpdateUI = () => {
            const noMinimumDiv = document.querySelector('.nominimum');
            const pageFooter = document.querySelector('.gform_page_footer');

            if (noMinimumDiv && pageFooter) {
                if (totalAmount < minimumSpend) {
                    noMinimumDiv.classList.remove('hidden');
                    pageFooter.classList.add('hidden');
                } else {
                    noMinimumDiv.classList.add('hidden');
                    pageFooter.classList.remove('hidden');
                }
                return true;
            }
            return false;
        };

        if (!checkAndUpdateUI()) {
            const intervalId = setInterval(() => {
                if (checkAndUpdateUI()) {
                    clearInterval(intervalId);
                }
            }, 100);

            return () => clearInterval(intervalId);
        }
    }, [priceSummary, tipAmount, ourDiscount, deliveryFee, complianceFee]);

    const onSubmit = async (data) => {
        // Define the submission logic here, including any GraphQL API calls
    };

    const handleButtonClick = () => {
        if (buttonText === 'Link New Account') {
            const addNewAccountButton = document.querySelector('.addnewstrong');
            if (addNewAccountButton) {
                addNewAccountButton.click();
            }
        } else if (buttonText === 'Pay with Stronghold') {
            const payWithStrongholdButton = document.querySelector('.paywithstronghold');
            if (payWithStrongholdButton) {
                payWithStrongholdButton.click();
            }
        } else {
            const originalButton = document.getElementById('gform_next_button_4_7') || document.getElementById('gform_next_button_5_7');
            if (originalButton) {
                originalButton.click();
            }
        }
    };

    useEffect(() => {
        const originalButton = document.getElementById('gform_next_button_4_7') || document.getElementById('gform_next_button_5_7');
        if (originalButton) {
            setButtonText(originalButton.value);

            const checkPageVisibilityAndUpdateButton = () => {
                const formPage5 = document.getElementById('gform_page_5_2');
                const formPage4 = document.getElementById('gform_page_4_2');
                const isPage2Visible = (formPage5 && formPage5.style.display !== 'none') || (formPage4 && formPage4.style.display !== 'none');

                const checkInputSelection = () => {
                    const selectedInput = document.querySelector('input[name="input_27"]:checked');

                    if (!selectedInput || selectedInput.value === 'new') {
                        setButtonText('Link New Account');
                    } else {
                        setButtonText('Pay with Stronghold');
                    }
                };

                if (isPage2Visible) {
                    checkInputSelection();
                } else {
                    setButtonText(originalButton.value);
                }
            };

            checkPageVisibilityAndUpdateButton();

            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                        setButtonText(originalButton.value);
                    }
                });
                checkPageVisibilityAndUpdateButton();
            });

            observer.observe(originalButton, {
                attributes: true
            });

            const radioButtons = document.querySelectorAll('input[name="input_27"]');
            radioButtons.forEach(radio => {
                radio.addEventListener('change', checkPageVisibilityAndUpdateButton);
            });

            const visibilityCheckInterval = setInterval(() => {
                checkPageVisibilityAndUpdateButton();
            }, 500);

            return () => {
                observer.disconnect();
                clearInterval(visibilityCheckInterval);
                radioButtons.forEach(radio => {
                    radio.removeEventListener('change', checkPageVisibilityAndUpdateButton);
                });
            };
        }
    }, []);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    return (
        <form id="react-checkout-form" onSubmit={handleSubmit(onSubmit)} className="flex-col mx-auto react-form-settings  rounded-lg">
            <h2 className="text-2xl font-bold font-good mb-4">Order Summary</h2>
            <div className="mb-4">
                {cartItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b">
                        <div className="flex items-center space-x-4">
                            <img src={item.product.image} alt={item.product.name} className="new-product-image w-16 h-16 object-cover rounded" />
                            <div>
                                <p className="font-semibold new-product-name">{item.product.name}</p>
                                <p className="text-sm text-gray-500 new-brand-name">{item.product.brand.name}</p>
                                <p className="text-sm text-gray-500 new-price">${item.product.variants[0].priceRec}</p>
                            </div>
                        </div>
                        <select
                            value={item.quantity}
                            onChange={(e) => handleChangeQuantity(item.id, parseInt(e.target.value))}
                            className="max-w-sm dropdown-checkout border px-2 py-1"
                            style={{ maxWidth: '58px' }}
                        >
                            {[...Array(10).keys()].map(i => (
                                <option key={i} value={i}>{i}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>

            {appliedDiscount ? (
                <div className="mb-4 flex items-center">
                    <span className="border px-2 py-4 mr-2 bg-green-100 text-green-700 rounded-md">
                        {appliedDiscount.code}
                        <button
                            type="button"
                            onClick={handleRemoveCoupon}
                            className="ml-2 text-red-500 hover:text-red-700"
                        >
                            &times;
                        </button>
                    </span>
                </div>
            ) : (
                <div className="mb-4 flex items-start">
                    <input
                        type="text"
                        placeholder="Coupon Code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="border px-2 py-4 mr-2"
                    />
                    <button
                        type="button"
                        onClick={handleApplyCoupon}
                        style={{
                            backgroundColor: '#072d41',
                            color: 'white'
                        }}
                        className="text-black py-4 px-4  bg-gray-800 text-white"
                    >
                        Apply
                    </button>
                </div>
            )}

            <div className="mb-4">
                <table className="w-full side-table-react">
                    <tbody>
                    <tr>
                        <td>Subtotal..</td>
                        <td className="text-right">${(priceSummary?.subtotal / 100).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>Taxes</td>
                        <td className="text-right">${(priceSummary?.taxes / 100).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>Discounts</td>
                        <td className="text-right">${((priceSummary?.discounts + ourDiscount) / 100).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>Delivery Fee</td>
                        <td className="text-right">${(deliveryFee / 100).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>Compliance Fee</td>
                        <td className="text-right">${complianceFee.toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>Tip Amount</td>
                        <td className="text-right">${(tipAmount / 100).toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td className="text-right-larger"><strong>Total</strong></td>
                        <td className="text-right text-right-larger" ><strong>${((priceSummary?.subtotal + priceSummary?.taxes - priceSummary?.discounts - ourDiscount + deliveryFee) / 100 + complianceFee + tipAmount / 100).toFixed(2)}</strong></td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <button
                type="button"
                className="gform_next_button gform-theme-button button flex items-center justify-center"
                onClick={handleButtonClick}
                disabled={loadingSpinner}
            >
                {loadingSpinner ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="w-6 h-6 spinner animate-spin"
                        fill="currentColor"
                    >
                        <path className="fa-secondary" opacity=".4" d="M0 256C0 114.9 114.1 .5 255.1 0C237.9 .5 224 14.6 224 32c0 17.7 14.3 32 32 32C150 64 64 150 64 256s86 192 192 192c69.7 0 130.7-37.1 164.5-92.6c-3 6.6-3.3 14.8-1 22.2c1.2 3.7 3 7.2 5.4 10.3c1.2 1.5 2.6 3 4.1 4.3c.8 .7 1.6 1.3 2.4 1.9c.4 .3 .8 .6 1.3 .9s.9 .6 1.3 .8c5 2.9 10.6 4.3 16 4.3c11 0 21.8-5.7 27.7-16c-44.3 76.5-127 128-221.7 128C114.6 512 0 397.4 0 256z"/>
                        <path className="fa-primary" d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z"/>
                    </svg>
                ) : (
                    buttonText
                )}
            </button>
        </form>
    );
};

export default CheckoutForm2;
